import { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { getObjectValueArrayForKey } from '../../../../functions/utils/getObjectValueArrayForKey';
import { LauncherElementData, useLauncherElementsStore } from '../../../../hooks/stores/launcherElementsStore';
import { LauncherWindowData, useLauncherWindowStore } from '../../../../hooks/stores/launcherWindowStore';
import { UIData, useUIStore } from '../../../../hooks/stores/uiStore';
import { ColorSwatch } from '../ColorSwatch';
import { DropdownInput } from '../DropdownInput';

export const ColorPickerModalColorSwatches = ({
  onChange,
  defaultSwatchesTarget,
}: {
  onChange: (newValue: string | undefined) => void;
  defaultSwatchesTarget?: string;
}) => {
  const [UIData] = useUIStore<UIData>();
  const [launcherElementsData] = useLauncherElementsStore<Record<string, LauncherElementData>>();
  const [launcherWindowData] = useLauncherWindowStore<LauncherWindowData>();
  const [swatchesTarget, setSwatchesTarget] = useState<string>(defaultSwatchesTarget || 'container');
  const [swatches, setSwatches] = useState<any>(null);

  const getColorsForKey = useCallback((key: string) => {
    const result: any = new Set();

    const filterColors = (colors: any[]) =>
      colors.filter(
        (color) => color !== undefined && color.length > 0 && color !== '#00000000' && color !== 'transparent'
      );

    // Window
    const windowStyle = launcherWindowData.class;
    const windowValues = getObjectValueArrayForKey(windowStyle, key);
    const windowFiltered = filterColors(windowValues);
    if (windowFiltered.length > 0) {
      windowFiltered.forEach((color: string) => result.add(color));
    }
    // Elements
    Object.values(launcherElementsData).forEach((element) => {
      const style = element.props.class;
      const elementValues = getObjectValueArrayForKey(style, key);
      const filtered = filterColors(elementValues);
      if (filtered.length > 0) {
        filtered.forEach((color: string) => result.add(color));
      }
    });

    return [...result];
  }, []);

  // Update color swatches
  useEffect(() => {
    const backgroundValues = getColorsForKey('background');
    const backgroundColorValues = getColorsForKey('backgroundColor');
    const container = [...new Set([...backgroundValues, ...backgroundColorValues])];
    const text = getColorsForKey('color');

    const document = [...new Set([...container, ...text])];

    setSwatches({ document, container, text });
  }, [UIData.selectedElementIDs]);

  return (
    <>
      <DropdownInput
        value={swatchesTarget}
        onChange={(newValue) => setSwatchesTarget(String(newValue))}
        options={[
          { value: 'document', label: 'Document Colors' },
          { value: 'container', label: 'Container Colors' },
          { value: 'text', label: 'Text Colors' },
        ]}
        canBeCleared={false}
        isSearchable={false}
      />

      <SwatchesContainer>
        {swatches?.[swatchesTarget]?.map((hex: string, index: number) => (
          <SwatchContainer key={`color-swatch-document-${hex}-${index}`} onClick={() => onChange(hex)}>
            <ColorSwatch value={hex} id={`color-swatch-${index}`} size={18} />
          </SwatchContainer>
        ))}
      </SwatchesContainer>
    </>
  );
};

const SwatchesContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;

  padding: 5px;
`;

const SwatchContainer = styled.div`
  cursor: pointer;
`;
